import { useSetting } from '@/composables/use-setting'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { ClientInfo, login, updateLoginUser } from '@voghion/native-api'
import { getSessionStorage, dispatchAppEvent } from '@voghion/fe-util'
import { autoRegisterUser, LoginResult, getLoginConfig } from '@/api/user'
import { useLogin } from '@/composables/use-login'
import { EVENT_UPDATE_USER_MESSAGE } from '@/utils/type'
import { RegisterType } from '@/components/sign-in/constant'

export type CheckLoginOptions = {
  autoLogin?: boolean // 是否自动登录，默认 true
  loginRedirect?: string // 登录后的回调
}

export enum LoginMethod {
  Email = 'Email',
  Google = 'Google',
  Facebook = 'Facebook',
  Phone = 'Phone'
}
/**
 * 用户
 */
export const useUserStore = defineStore('user', () => {
  const session = getSessionStorage()
  const setting = useSetting()
  const autoRegisterLoading = ref(false)
  const shouldCheckMessage = ref(false)
  /**
   * 设备信息
   */
  const clientInfo = ref<{
    userToken: string
  }>({
    userToken: ''
  })
  /**
   * 是否已登录
   */
  const isLogin = computed(() => {
    return !!clientInfo.value.userToken
  })
  /**
   * 用户 token
   */
  const userToken = computed(() => {
    return clientInfo.value.userToken
  })
  
  /**
   * 检查登录
   * @returns
   */
  const checkLogin = (options: { showPcLogin?: boolean } = {}) => {
    return new Promise((resolve, reject) => {
      if (isLogin.value) {
        resolve(true)
      } else {
        login({ options }).then((res) => {
          if (res.data) {
            clientInfo.value.userToken = res.data.userToken
            resolve(true)
          } else {
            reject(res.error)
          }
        }, reject)
      }
    })
  }
  const expiredKey = 'expired_user_token'
  /**
   * 登录过期
   */
  const loginExpired = () => {
    const userToken = clientInfo.value.userToken
    session.setItem(expiredKey, userToken)
    clientInfo.value.userToken = ''
  }
  /**
   * 更新 clientInfo
   * @param value
   */
  const updateClientInfo = (value: ClientInfo) => {
    const finalValue = { ...value }
    const expiredToken = session.getItem(expiredKey)
    if (expiredToken && finalValue.userToken === expiredToken) {
      finalValue.userToken = ''
    }
    setting.updateClientInfo(finalValue)
    clientInfo.value = finalValue
  }

  /**
   * 自动注册
   * @param email
   */
  const autoRegister = (email: string = '', registerType) => {
    return new Promise((resolve, reject) => {
      autoRegisterLoading.value = true
      autoRegisterUser({ email, registerType }).then((res) => {
        if (res.data) {
          clientInfo.value.userToken = res.data.userToken
          // 更新 客户端/PC 信息
          updateLoginUser(res.data)
          // 更新message center的用户信息
          dispatchAppEvent(EVENT_UPDATE_USER_MESSAGE, res.data)
          resolve(res)
        } else {
          reject(res)
        }
      }).catch(reject).finally(() => {
        const delay = registerType === RegisterType.AUTO ? 500 : 2500
        setTimeout(() => {
          autoRegisterLoading.value = false
        }, delay);
      })
    })
  }


  const afterLoginSuccess = (
    loginResult
  ) => {
    autoRegisterLoading.value = true
    if (loginResult.userToken) {
      clientInfo.value.userToken = loginResult.userToken
      updateLoginUser(loginResult)
      dispatchAppEvent(EVENT_UPDATE_USER_MESSAGE, loginResult)
    }
    setTimeout(() => {
      autoRegisterLoading.value = false
      // 加载最大时间
    }, 2500)
  }

  const phoneLoginCountryAuthList = ref([])

  const loadLoginConfig = async (params: object) => {
    const res = await getLoginConfig(params)
    const obj = res?.data || {}
    const { phoneLoginCountry } = obj
    phoneLoginCountryAuthList.value = phoneLoginCountry ? JSON.parse(phoneLoginCountry) : []
    return res
  }

  return {
    isLogin,
    userToken,
    autoRegisterLoading,

    shouldCheckMessage,
    phoneLoginCountryAuthList,
    checkLogin,
    loginExpired,
    updateClientInfo,
    autoRegister,
    afterLoginSuccess,
    loadLoginConfig
  }
})
